<template>
  <div class="list-preorder">
    <KTCodePreview v-bind:title="'Danh sách đơn đặt trước'">
      <template v-slot:toolbar v-if="haveHeaderActions">
        <b-row>
          <b-dropdown size="sm" right v-show="canExport">
            <template slot="button-content">
              <i style="font-size: 1rem" class="fas fa-cog"></i>
              <span class="font-weight-bolder">Thao tác</span>
            </template>
            <b-dropdown-item @click="exportExcel" v-show="canExport">
              <span>
                <i style="font-size: 1rem" class="far fa-file-excel"></i>
                &nbsp; Xuất Excel</span
              >
            </b-dropdown-item>
          </b-dropdown>

          <b-button
            class="font-weight-bolder ml-3"
            @click="createNew"
            size="sm"
            variant="primary"
            v-show="canCreate"
          >
            <i style="font-size: 1rem" class="flaticon2-add-1"></i> Tạo mới
          </b-button>
        </b-row>
      </template>

      <template v-slot:preview>
        <div class="preorder-filters mb-3">
          <b-row class="mb-3">
            <b-col md="3">
              <BDropdownCustom
                v-model="filters.storeIds"
                :options="storeOptions"
                :multiple="true"
                :searchable="true"
                value-prop="id"
                text-prop="name"
                placeholder="Cửa hàng"
                :matchKey="['name']"
                :limit="5"
              />
            </b-col>

            <b-col md="3" class="date-range-col">
              <div class="d-flex w-100">
                <b-input-group>
                  <date-picker
                    placeholder="Từ"
                    class="form-control form-control-sm"
                    v-model="filters.createdFrom"
                    :config="configs.date"
                  />
                </b-input-group>
                <span class="ml-1 mr-1"></span>
                <b-input-group>
                  <date-picker
                    placeholder="Đến"
                    class="form-control form-control-sm"
                    v-model="filters.createdTo"
                    :config="configs.date"
                  />
                </b-input-group>
              </div>
            </b-col>

            <b-col md="3">
              <BDropdownCustom
                v-model="filters.preorderProgramIds"
                :options="programOptions"
                :multiple="true"
                :searchable="true"
                value-prop="id"
                text-prop="name"
                placeholder="chương trình"
                :matchKey="['name']"
                :limit="5"
              />
            </b-col>

            <b-col md="3">
              <BDropdownCustom
                v-model="filters.statusIds"
                :options="statusOptions"
                :multiple="true"
                :searchable="false"
                value-prop="value"
                text-prop="text"
                placeholder="Trạng thái"
              />
            </b-col>
          </b-row>

          <b-row class="mb-3">
            <b-col md="3">
              <b-form-input
                size="sm"
                placeholder="ID đơn hàng"
                v-model.trim="filters.convertedOrderId"
                @keyup.enter="onFilter"
                autocomplete="off"
              />
            </b-col>

            <b-col md="3">
              <b-form-input
                size="sm"
                placeholder="ID đơn đặt trước"
                v-model.trim="filters.id"
                @keyup.enter="onFilter"
                autocomplete="off"
              />
            </b-col>

            <b-col md="3">
              <b-form-input
                size="sm"
                placeholder="Tên / SĐT khách"
                v-model.trim="filters.customerKeyword"
                @keyup.enter="onFilter"
                autocomplete="off"
              />
            </b-col>

            <b-col md="3">
              <b-form-input
                size="sm"
                placeholder="Tìm theo sản phẩm"
                v-model.trim="filters.productKeyword"
                @keyup.enter="onFilter"
                autocomplete="off"
              />
            </b-col>
          </b-row>

          <b-row class="mb-0">
            <b-col md="3">
              <b-form-input
                size="sm"
                placeholder="Ghi chú"
                v-model.trim="filters.noteKeyword"
                @keyup.enter="onFilter"
                autocomplete="off"
              />
            </b-col>
            <b-col md="3">
              <BDropdownCustom
                v-model="filters.typeIds"
                :options="typeOptions"
                :multiple="true"
                :searchable="false"
                value-prop="value"
                text-prop="text"
                placeholder="loại đơn"
                :limit="5"
              />
            </b-col>
            <b-col md="3">
              <AutoSuggest
                :model="staffSearchText"
                :suggestions="staffSuggestions"
                placeholder="Nhân viên"
                :suggestion-name="'label'"
                @change="searchStaff"
                @select="selectStaff"
              >
                <div slot="custom" slot-scope="{ suggestion }">
                  {{ suggestion.item.label }} - {{ suggestion.item.code }}
                </div>
              </AutoSuggest>
            </b-col>
          </b-row>
        </div>
        <b-row class="mb-3">
          <b-col md="3" class="d-flex align-items-center">
            <b-button
              size="sm"
              variant="primary"
              @click="onFilter"
              style="font-weight: 600; width: 70px"
            >
              Lọc
            </b-button>
            <b-button
              size="sm"
              variant="secondary"
              class="ml-2"
              @click="resetFilter"
            >
              <i class="fa fa-redo"></i> Reset
            </b-button>
          </b-col>
        </b-row>

        <b-table
          :items="preorders"
          :fields="fields"
          class="table-bordered table-hover"
          no-local-sorting
          :small="true"
          :busy="loading"
          responsive
          show-empty
          empty-text="Không có dữ liệu"
        >
          <template v-slot:table-busy>
            <vcl-table :speed="5" :animate="true" :columns="8"></vcl-table>
          </template>

          <template v-slot:cell(createdAtStaffStore)="row">
            <div class="cell-stack">
              <div class="cell-main">
                {{ formatDate(row.item.createdAt, 'DD-MM-YYYY HH:mm') || '-' }}
              </div>
              <div class="cell-sub text-muted" v-if="row.item.staff?.fullName">
                {{ row.item.staff.fullName }}
              </div>
              <div
                class="cell-sub text-muted"
                v-if="row.item.store?.name || row.item.store?.shortName"
              >
                {{ row.item.store.name || row.item.store.shortName }}
              </div>
            </div>
          </template>

          <template v-slot:cell(id)="row">
            <div class="cell-stack">
              <div class="cell-main">
                <router-link
                  class="font-weight-bolder"
                  :to="{ name: 'edit-preorder', params: { id: row.item.id } }"
                >
                  {{ row.item.id }}
                </router-link>
              </div>
              <div class="cell-sub text-muted small">
                {{ getOrderTypeText(row.item.preorderProgram?.type) }}
              </div>
            </div>
          </template>

          <template v-slot:cell(customer)="row">
            <div class="cell-stack">
              <div class="cell-main font-weight-bolder">
                {{ row.item.customerName || '-' }}
              </div>
              <div class="cell-sub" v-if="row.item.customerPhone">
                {{ row.item.customerPhone }}
              </div>
            </div>
          </template>

          <template v-slot:cell(productSummary)="row">
            <div class="cell-stack">
              <div class="cell-main" v-if="row.item.targetProductName">
                <span class="text-muted">Sản phẩm: </span>
                <span class="font-weight-bolder">{{
                  row.item.targetProductName
                }}</span>
              </div>
              <div class="cell-sub" v-if="row.item.tradeinProductName">
                <span class="text-muted">Thu cũ: </span>
                <span>{{ row.item.tradeinProductName }}</span>
              </div>
              <div
                class="cell-sub"
                v-if="
                  row.item.depositAmount && Number(row.item.depositAmount) > 0
                "
              >
                <span class="text-muted">Cọc: </span>
                <span class="font-weight-bolder">
                  {{ formatPrice(row.item.depositAmount) }}
                </span>
              </div>
              <div
                class="cell-sub text-muted"
                v-if="
                  !row.item.targetProductName &&
                  !row.item.tradeinProductName &&
                  !(
                    row.item.depositAmount && Number(row.item.depositAmount) > 0
                  )
                "
              >
                -
              </div>
            </div>
          </template>

          <template v-slot:cell(programName)="row">
            <span
              class="text-primary cursor-pointer font-weight-bolder"
              v-if="row.item.preorderProgram && row.item.preorderProgram.id"
              @click="filterByProgram(row.item.preorderProgram.id)"
            >
              {{ row.item.preorderProgram.name || '-' }}
            </span>
            <span v-else>-</span>
          </template>

          <template v-slot:cell(note)="row">
            <span v-b-tooltip.hover :title="row.item.note || ''">
              {{ truncate(row.item.note, 30) }}
            </span>
          </template>

          <template v-slot:cell(status)="row">
            <b-badge :class="getStatusClass(row.item.status) + ' text-white'">
              {{ getStatusText(row.item.status) }}
            </b-badge>
            <div class="cell-sub mt-1" v-if="row.item.convertedOrderId">
              <router-link
                :to="{
                  name: 'edit-order',
                  params: { id: row.item.convertedOrderId },
                }"
                class="font-weight-bolder"
                v-b-tooltip.hover.left
                title="Đơn hàng"
              >
                <i
                  class="menu-icon flaticon2-supermarket text-primary mt-2"
                  aria-hidden="true"
                ></i>
                {{ row.item.convertedOrderId }}
              </router-link>
            </div>
          </template>

          <template v-slot:cell(actions)="row">
            <b-dropdown size="sm" no-caret right lazy variant="link">
              <template #button-content>
                <i style="font-size: 1rem" class="flaticon2-settings pr-0"></i>
              </template>

              <b-dropdown-item
                @click="
                  $router.push({
                    name: 'edit-preorder',
                    params: { id: row.item.id },
                  })
                "
              >
                <span style="color: #3f4254; font-size: 12px">
                  <i style="font-size: 1rem" class="flaticon2-pen"></i>
                  &nbsp; Chỉnh sửa
                </span>
              </b-dropdown-item>

              <b-dropdown-item
                v-if="canConvert && canShowConvert(row.item)"
                @click="openConvertModal(row.item)"
              >
                <span style="color: #3f4254; font-size: 12px">
                  <i style="font-size: 1rem" class="flaticon2-refresh"></i>
                  &nbsp; Chuyển đơn hàng
                </span>
              </b-dropdown-item>
            </b-dropdown>
          </template>
        </b-table>

        <b-row class="mt-3 align-items-center">
          <b-col md="6" class="mb-2 mb-md-0">
            <span class="text-dark preorder-total">
              Tổng số: <b>{{ paginations.total || 0 }}</b>
            </span>
          </b-col>
          <b-col md="6" class="d-flex justify-content-md-end">
            <b-pagination
              v-if="paginations.totalPages > 1"
              v-model="paginations.currentPage"
              :total-rows="paginations.total"
              :per-page="perPage"
              size="sm"
              @input="onPageChange"
            />
          </b-col>
        </b-row>
      </template>
    </KTCodePreview>

    <ConvertPreorderModal
      :show="showConvertModal"
      :preorder="selectedPreorder"
      :convert-loading="convertLoading"
      @close="closeConvertModal"
      @confirm="handleConvert"
    />
  </div>
</template>

<script>
import KTCodePreview from '@/view/content/CodePreview.vue';
import { VclTable } from 'vue-content-loading';
import BDropdownCustom from '@/view/base/bootstrap/BDropdownCustom.vue';
import AutoSuggest from '@/view/base/auto-sugguest/AutoSuggest.vue';
import ConvertPreorderModal from './components/ConvertPreorderModal.vue';

import PreorderService from '@/core/api/preorder/preorder.service';
import PreorderProgramService from '@/core/api/preorder/preorder-program.service';
import { UserService } from '@/core/api/user/user.service';
import ApiService from '@/core/services/api.service';

import { PreOrderModel } from '@/core/models/PreOrder.model';
import UserModel from '@/core/models/User.model';

import { DP_CONFIG, formatDate, formatDBDate } from '@/utils/date';
import { createFile, getFileName } from '@/utils/file-download';
import { formatPrice, makeToastFaile, makeToastSuccess } from '@/utils/common';
import { permissionMixin } from '@/view/mixins/permission.mixins';
import { SET_BREADCRUMB } from '@/core/services/store/modules/breadcrumbs.module';
import * as ENUM from '@/utils/enum';
import * as ENUM_NAME from '@/utils/enum-name';
import _ from 'lodash';

export default {
  name: 'ListPreorder',
  mixins: [permissionMixin],
  components: {
    KTCodePreview,
    VclTable,
    BDropdownCustom,
    AutoSuggest,
    ConvertPreorderModal,
  },
  data() {
    return {
      preorders: [],
      loading: false,
      perPage: 10,
      filters: {
        id: '',
        typeIds: [],
        storeIds: [],
        createdFrom: '',
        createdTo: '',
        convertedOrderId: '',
        staffId: null,
        customerKeyword: '',
        productKeyword: '',
        preorderProgramIds: [],
        statusIds: [],
        noteKeyword: '',
      },
      storeOptions: [],
      programOptions: [],
      typeOptions: [],
      statusOptions: [],
      staffSearchText: '',
      staffSuggestions: [],
      searchTimeout: null,
      paginations: {
        total: 0,
        currentPage: 1,
        totalPages: 1,
      },
      configs: DP_CONFIG,
      PERMISSIONS: ENUM.PERMISSIONS,
      showConvertModal: false,
      selectedPreorder: null,
      convertLoading: false,
      fields: [
        {
          key: 'createdAtStaffStore',
          label: 'Ngày tạo',
          thStyle: { textAlign: 'center', width: '11%' },
        },
        {
          key: 'id',
          label: 'ID',
          thStyle: { textAlign: 'center', width: '8%' },
          tdClass: 'text-center',
        },
        {
          key: 'customer',
          label: 'Khách hàng',
          thStyle: { textAlign: 'left', width: '9%' },
        },
        {
          key: 'productSummary',
          label: 'Sản phẩm khách muốn sở hữu',
          thStyle: { textAlign: 'left' },
        },
        {
          key: 'programName',
          label: 'Chương trình',
          thStyle: { textAlign: 'left' },
          tdClass: 'text-wrap',
        },
        { key: 'note', label: 'Ghi chú', thStyle: { textAlign: 'left' } },
        {
          key: 'status',
          label: 'Trạng thái',
          thStyle: { textAlign: 'center', width: '8%' },
          tdClass: 'text-center',
        },
        {
          key: 'actions',
          label: ' ',
          thStyle: { textAlign: 'center', width: '3%' },
          tdClass: 'text-center',
        },
      ],
    };
  },
  computed: {
    canView() {
      return this.hasPermission(this.PERMISSIONS.PRE_ORDER_VIEW);
    },
    canCreate() {
      return this.hasPermission(this.PERMISSIONS.PRE_ORDER_FORM_CREATE);
    },
    canExport() {
      return this.hasPermission(this.PERMISSIONS.PRE_ORDER_EXPORT);
    },
    canConvert() {
      return this.hasPermission(this.PERMISSIONS.PRE_ORDER_CONVERT);
    },
    haveHeaderActions() {
      return this.canCreate || this.canExport;
    },

    preorderProgramIdParam() {
      const arr = this.filters.preorderProgramIds;
      if (!Array.isArray(arr) || !arr.length) return undefined;
      return arr
        .map((p) => (p && p.id != null ? Number(p.id) : null))
        .filter((id) => id != null);
    },
  },
  created() {
    if (!this.canView) {
      this.$router.replace({ name: 'dashboard' });
    }
    this.statusOptions = this.getOptions(
      ENUM.PREORDER_STATUS,
      ENUM_NAME.PREORDER_STATUS_NAME,
    );
    this.typeOptions = this.getOptions(
      ENUM.PREORDER_TYPE,
      ENUM_NAME.PREORDER_TYPE_NAME,
    );
    this.loadStores();
    this.loadPrograms();
  },
  mounted() {
    this.setBreadCrumbs();
    this.fetchData();
  },
  methods: {
    makeToastSuccess,
    makeToastFaile,
    formatDate,
    formatPrice,

    setBreadCrumbs() {
      this.$store.dispatch(SET_BREADCRUMB, [
        { title: 'Đơn đặt trước', route: '/preorder' },
        { title: 'Danh sách', active: true },
      ]);
    },

    getOptions(enumValues, enumNames) {
      if (!enumValues || !enumNames) return [];
      const options = Object.values(enumValues).map((val) => ({
        value: val,
        text: enumNames[val] || val,
      }));
      return options;
    },

    async loadStores() {
      try {
        const { data } = await ApiService.get('stores/get-by-user');
        const stores = data?.data?.stores || data?.stores || [];
        this.storeOptions = stores.map((s) => ({
          id: s.id,
          name: s.name || s.shortName || s.id,
        }));
      } catch (e) {
        console.error('Load stores failed', e);
      }
    },

    async loadPrograms() {
      try {
        const { data } = await PreorderProgramService.list();
        const list = data?.data || data || [];
        this.programOptions = list.map((p) => ({ id: p.id, name: p.name }));
      } catch (e) {
        console.error('Load programs failed', e);
      }
    },

    selectStaff(option) {
      if (option?.item) {
        this.filters.staffId = option.item.id;
        this.staffSearchText = option.item.label;
      } else {
        this.filters.staffId = null;
        this.staffSearchText = '';
      }
    },

    searchStaff(searchQuery) {
      if (this.searchTimeout) clearTimeout(this.searchTimeout);
      this.searchTimeout = setTimeout(
        () => this.performSearchStaff(searchQuery),
        300,
      );
    },

    async performSearchStaff(q) {
      this.staffSuggestions = [];
      if (!q || q.length < 2) return;
      try {
        const { data } = await UserService.smartSearch({
          keyword: q,
          limit: 5,
        });
        const users =
          UserModel.fromJsonArray(data?.data?.data || data?.data || []) || [];
        this.staffSuggestions = users.map((u) => ({
          id: u.id,
          label: u.fullName,
          name: u.fullName,
          code: u.code,
        }));
      } catch (e) {
        console.error('Search staff failed', e);
      }
    },

    formatCreatedAtStaffStore(item) {
      const d = item.createdAt
        ? formatDate(item.createdAt, 'DD/MM/YYYY HH:mm')
        : '';
      const staff =
        item.staff && item.staff.fullName ? item.staff.fullName : '';
      const store =
        item.store && (item.store.name || item.store.shortName)
          ? item.store.name || item.store.shortName
          : '';
      const parts = [d];
      if (staff) parts.push(`Nhân viên: ${staff}`);
      if (store) parts.push(`Cửa hàng: ${store}`);
      return parts.join(' - ');
    },

    truncate(str, max) {
      if (!str) return '';
      return str.length <= max ? str : str.slice(0, max) + '...';
    },

    getStatusClass(status) {
      const s = Number(status);
      const map = {
        [ENUM.PREORDER_STATUS.New]: 'warning',
        [ENUM.PREORDER_STATUS.InvalidData]: 'danger',
        [ENUM.PREORDER_STATUS.DuplicateData]: 'warning',
        [ENUM.PREORDER_STATUS.NeedMoreInfo]: 'info',
        [ENUM.PREORDER_STATUS.AgreeDeposit]: 'warning',
        [ENUM.PREORDER_STATUS.Deposited]: 'success',
        [ENUM.PREORDER_STATUS.NotReached]: 'info',
        [ENUM.PREORDER_STATUS.NoLongerNeed]: 'dark',
        [ENUM.PREORDER_STATUS.NotAcceptTradeIn]: 'dark',
        [ENUM.PREORDER_STATUS.OutOfStock]: 'warning',
        [ENUM.PREORDER_STATUS.NoNeed]: 'secondary',
        [ENUM.PREORDER_STATUS.Quoted]: 'info',
        [ENUM.PREORDER_STATUS.TradeInDone]: 'success',
        [ENUM.PREORDER_STATUS.Failed]: 'danger',
      };
      return 'badge badge-' + (map[s] || 'secondary');
    },

    getStatusText(status) {
      return ENUM_NAME.PREORDER_STATUS_NAME[status] || status || '-';
    },

    getOrderTypeText(type) {
      return ENUM_NAME.PREORDER_TYPE_NAME[type] || '-';
    },

    filterByProgram(programId) {
      if (!programId) {
        this.filters.preorderProgramIds = [];
      } else {
        const program = this.programOptions.find(
          (p) => p.id === programId || p.id === Number(programId),
        );
        this.filters.preorderProgramIds = program ? [program] : [];
      }
      this.onFilter();
    },

    canShowConvert(item) {
      return item.targetProductId && !item.convertedOrderId;
    },

    openConvertModal(item) {
      this.selectedPreorder = item;
      this.showConvertModal = true;
    },

    closeConvertModal() {
      this.showConvertModal = false;
      this.selectedPreorder = null;
    },

    async handleConvert(payload) {
      if (!this.selectedPreorder) return;
      this.convertLoading = true;
      try {
        const res = await PreorderService.convert(
          this.selectedPreorder.id,
          payload,
        );
        const order = res?.data?.data || res?.data || res;
        const orderId = order?.id ?? order;
        makeToastSuccess('Chuyển đơn hàng thành công');
        this.closeConvertModal();
        await this.fetchData();

        if (orderId) {
          this.$router.push({ name: 'edit-order', params: { id: orderId } });
        }
      } catch (e) {
        makeToastFaile(e?.response?.data?.message || 'Chuyển đơn thất bại');
      } finally {
        this.convertLoading = false;
      }
    },

    buildParams(page = 1) {
      const createdFrom = this.filters.createdFrom
        ? formatDBDate(this.filters.createdFrom)
        : undefined;
      const createdTo = this.filters.createdTo
        ? formatDBDate(this.filters.createdTo)
        : undefined;
      const statusIds = Array.isArray(this.filters.statusIds)
        ? this.filters.statusIds.map((s) => (s && s.value) || s)
        : [];
      const storeIds =
        Array.isArray(this.filters.storeIds) && this.filters.storeIds.length
          ? this.filters.storeIds.map((s) =>
              s && typeof s === 'object' && s.id != null ? s.id : s,
            )
          : undefined;

      const typeIds = Array.isArray(this.filters.typeIds)
        ? this.filters.typeIds
            .map((t) => (t && t.value != null ? t.value : t))
            .filter((v) => v != null)
        : [];
      const idParam = this.filters.id ? Number(this.filters.id) : undefined;
      return _.omitBy(
        {
          id: idParam,
          typeIds: typeIds.length ? typeIds : undefined,
          storeIds,
          createdFrom,
          createdTo,
          convertedOrderId: this.filters.convertedOrderId
            ? Number(this.filters.convertedOrderId)
            : undefined,
          staffId: this.filters.staffId,
          customerKeyword: this.filters.customerKeyword || undefined,
          productKeyword: this.filters.productKeyword || undefined,
          preorderProgramIds: this.preorderProgramIdParam,
          statusIds: statusIds.length ? statusIds : undefined,
          noteKeyword: this.filters.noteKeyword || undefined,
          page,
          limit: this.perPage,
        },
        (v) => v === undefined || v === null || v === '',
      );
    },

    async fetchData(page = 1) {
      this.loading = true;
      try {
        const params = this.buildParams(page);
        const { data } = await PreorderService.getAll(params);
        const list = data?.data?.data || [];
        this.preorders = PreOrderModel.fromJsonArray(list);
        this.paginations = data.paginations || {
          total: list.length,
          currentPage: page,
          totalPages: 1,
        };
      } catch (e) {
        console.error('Fetch preorders failed', e);
        makeToastFaile('Có lỗi khi tải dữ liệu');
      } finally {
        this.loading = false;
      }
    },

    onFilter() {
      this.paginations.currentPage = 1;
      this.fetchData(1);
    },

    resetFilter() {
      this.filters = {
        id: '',
        typeIds: [],
        storeIds: [],
        createdFrom: '',
        createdTo: '',
        convertedOrderId: '',
        staffId: null,
        customerKeyword: '',
        productKeyword: '',
        preorderProgramIds: [],
        statusIds: [],
        noteKeyword: '',
      };
      this.staffSearchText = '';
      this.paginations = { total: 0, currentPage: 1, totalPages: 1 };
      this.fetchData(1);
    },

    onPageChange(page) {
      this.fetchData(page);
    },

    createNew() {
      this.$router.push({ name: 'add-preorder' });
    },

    async exportExcel() {
      try {
        const params = this.buildParams(1);
        const res = await PreorderService.exportExcel(params);
        const blob = res.data || res;
        const name =
          getFileName(res, 'don-dat-truoc.xlsx') ||
          `don-dat-truoc-${Date.now()}.xlsx`;
        createFile(blob, name);
        makeToastSuccess('Xuất Excel thành công');
      } catch (e) {
        console.error('Error exporting excel:', e);
        makeToastFaile(e?.response?.data?.message || 'Xuất Excel thất bại');
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.preorder-filters {
  .preorder-filter-hint {
    font-size: 12px;
  }

  .date-range-col {
    .input-group {
      flex: 1 1 0;
      min-width: 0;
    }
  }
}

.cell-stack {
  .cell-main {
    line-height: 1.2;
  }
  .cell-sub {
    line-height: 1.2;
    font-size: 12px;
  }
}

.preorder-total {
  font-weight: 500;
}
</style>
